import {
  syncFollowUpVisibility,
  syncAllFollowUpVisibility,
  findFollowUpChecked
} from "./followUps";
import { buildSelections } from "./selections";
import { fetchProductSelectorScore } from "./fetchScore";
import {
  pushProductSelectorEvent,
  buildStepCompletePayload,
  buildResultsEventPayload
} from "./analytics";

const SEL = {
  root: ".product-selector-page",
  section: ".product-selector-page__question",
  intro: "#product-selector-introduction",
  introCta: ".product-selector-page__introduction-cta",
  questionsWrap: "#product-selector-questions",
  results: "#product-selector-results",
  loading: "#product-selector-results-loading",
  restart: "#product-selector-restart",
  restartConfirm: "#product-selector-restart-confirm",
  inlineError: "#product-selector-inline-error",
  confirmBtn: ".product-selector-page__confirm-options",
  resultProductLink: "[data-ps-result-product-id]"
};

/**
 * @param {Element} section
 * @returns {boolean}
 */
function isStepComplete(section) {
  const type = section.getAttribute("data-question-type") || "";

  if (type === "single-choice") {
    const main = section.querySelector("input[data-ps-main]:checked");

    if (!main) {
      return false;
    }

    const idx = main.getAttribute("data-answer-index");
    const fuBlock = section.querySelector(
      '.follow-up[data-parent-answer-index="' + idx + '"]'
    );

    if (fuBlock && !fuBlock.hasAttribute("hidden")) {
      return !!findFollowUpChecked(section, parseInt(idx, 10));
    }

    return true;
  }

  if (type === "multiple-choice") {
    const mains = section.querySelectorAll("input[data-ps-main]:checked");

    if (mains.length === 0) {
      return false;
    }

    for (let j = 0; j < mains.length; j++) {
      const answerIndexMc = parseInt(
        mains[j].getAttribute("data-answer-index"),
        10
      );
      const fuBlock = section.querySelector(
        '.follow-up[data-parent-answer-index="' + answerIndexMc + '"]'
      );

      if (fuBlock && !fuBlock.hasAttribute("hidden")) {
        if (!findFollowUpChecked(section, answerIndexMc)) {
          return false;
        }
      }
    }

    return true;
  }

  return false;
}

/**
 * @param {object} cfg ignitedProductSelector
 */
export function initProductSelectorWizard(cfg) {
  const root = document.querySelector(SEL.root);
  const questionsWrap = document.querySelector(SEL.questionsWrap);
  const resultsPanel = document.getElementById("product-selector-results");
  const loadingEl = document.getElementById("product-selector-results-loading");
  const resultsContentEl = document.getElementById(
    "product-selector-results-content"
  );
  const afterResultsEl = document.getElementById(
    "product-selector-after-results"
  );
  const restartBtn = document.getElementById("product-selector-restart");
  const inlineErrorEl = document.getElementById(
    "product-selector-inline-error"
  );
  const i18n = cfg && cfg.i18n ? cfg.i18n : null;

  if (
    !root ||
    !questionsWrap ||
    !resultsPanel ||
    !loadingEl ||
    !resultsContentEl ||
    !restartBtn ||
    !cfg ||
    !cfg.ajaxUrl ||
    !cfg.action ||
    !cfg.nonce ||
    !i18n ||
    typeof i18n.stepIncompleteSingle !== "string" ||
    typeof i18n.stepIncompleteMultiple !== "string"
  ) {
    return;
  }

  function hideAfterResults() {
    if (afterResultsEl) {
      afterResultsEl.setAttribute("hidden", "");
    }
  }

  function showAfterResults() {
    if (afterResultsEl) {
      afterResultsEl.removeAttribute("hidden");
    }
  }

  const sections = root.querySelectorAll(SEL.section);

  if (sections.length === 0) {
    return;
  }

  const restartConfirmEl = document.querySelector(SEL.restartConfirm);
  const introEl = document.querySelector(SEL.intro);
  const hasIntro = !!introEl;
  const stepsTotal = sections.length;
  let currentStep = 0;
  let showingIntro = hasIntro;
  let showingResults = false;
  /** @type {AbortController|null} */
  let fetchAbort = null;

  restartBtn.disabled = false;

  pushProductSelectorEvent(cfg, "product_selector_view", {
    // eslint-disable-next-line camelcase
    has_intro: hasIntro,
    // eslint-disable-next-line camelcase
    step_count: stepsTotal
  });

  if (!hasIntro) {
    pushProductSelectorEvent(cfg, "product_selector_start", {
      // eslint-disable-next-line camelcase
      step_count: stepsTotal
    });
  }

  function getRestartFromStep() {
    if (showingResults) {
      return "results";
    }

    if (showingIntro) {
      return "intro";
    }

    return currentStep + 1;
  }

  function hideInlineError() {
    if (inlineErrorEl) {
      inlineErrorEl.textContent = "";
      inlineErrorEl.setAttribute("hidden", "");
    }
  }

  function showInlineError(message) {
    if (inlineErrorEl) {
      inlineErrorEl.textContent = message;
      inlineErrorEl.removeAttribute("hidden");
    }
  }

  function hideAllQuestions() {
    for (let i = 0; i < sections.length; i++) {
      sections[i].setAttribute("hidden", "");
    }
  }

  function updateStepVisibility() {
    if (showingResults) {
      return;
    }

    if (showingIntro) {
      if (introEl) {
        introEl.removeAttribute("hidden");
      }

      hideAllQuestions();

      return;
    }

    if (introEl) {
      introEl.setAttribute("hidden", "");
    }

    for (let i = 0; i < sections.length; i++) {
      if (i === currentStep) {
        sections[i].removeAttribute("hidden");
      } else {
        sections[i].setAttribute("hidden", "");
      }
    }
  }

  function focusCurrentStepTitle() {
    const sec = sections[currentStep];

    if (!sec) {
      return;
    }

    const heading = sec.querySelector(".product-selector-page__question-title");

    if (heading && heading.focus) {
      heading.focus();
    }
  }

  function leaveIntro() {
    if (!showingIntro) {
      return;
    }

    showingIntro = false;
    currentStep = 0;
    pushProductSelectorEvent(cfg, "product_selector_start", {
      // eslint-disable-next-line camelcase
      step_count: stepsTotal
    });
    updateStepVisibility();
    focusCurrentStepTitle();
  }

  function resetWizard() {
    if (fetchAbort) {
      fetchAbort.abort();
      fetchAbort = null;
    }

    showingResults = false;
    showingIntro = hasIntro;
    currentStep = 0;

    const inputs = questionsWrap.querySelectorAll(
      "input[data-ps-main], input[data-ps-follow-up]"
    );

    for (let i = 0; i < inputs.length; i++) {
      inputs[i].checked = false;
    }

    questionsWrap.removeAttribute("hidden");
    resultsPanel.setAttribute("hidden", "");
    loadingEl.setAttribute("hidden", "");
    resultsContentEl.innerHTML = "";
    hideAfterResults();
    resultsPanel.setAttribute("aria-busy", "false");

    hideInlineError();

    syncAllFollowUpVisibility(root);
    updateStepVisibility();

    if (showingIntro && introEl) {
      const introTitle = document.getElementById(
        "product-selector-introduction-title"
      );

      if (introTitle && introTitle.focus) {
        introTitle.focus();
      }

      return;
    }

    pushProductSelectorEvent(cfg, "product_selector_start", {
      // eslint-disable-next-line camelcase
      step_count: stepsTotal
    });

    const firstTitle = document.getElementById("selector-q-1");

    if (firstTitle && firstTitle.focus) {
      firstTitle.focus();
    }
  }

  function openRestartConfirm() {
    if (!restartConfirmEl) {
      resetWizard();

      return;
    }

    restartConfirmEl.removeAttribute("hidden");
    const confirmEl = restartConfirmEl.querySelector(
      "[data-ps-restart-confirm]"
    );

    if (confirmEl && confirmEl.focus) {
      confirmEl.focus();
    }
  }

  function closeRestartConfirm() {
    if (!restartConfirmEl) {
      return;
    }

    restartConfirmEl.setAttribute("hidden", "");

    if (restartBtn && restartBtn.focus) {
      restartBtn.focus();
    }
  }

  function finishQuestions() {
    showingResults = true;
    showingIntro = false;
    hideInlineError();

    if (introEl) {
      introEl.setAttribute("hidden", "");
    }

    questionsWrap.setAttribute("hidden", "");
    resultsPanel.removeAttribute("hidden");
    loadingEl.removeAttribute("hidden");
    resultsContentEl.innerHTML = "";
    hideAfterResults();
    resultsPanel.setAttribute("aria-busy", "true");

    const built = buildSelections(sections);

    if (!built.ok) {
      loadingEl.setAttribute("hidden", "");
      resultsPanel.setAttribute("aria-busy", "false");
      resultsContentEl.textContent = JSON.stringify(
        built.errorPayload,
        null,
        2
      );
      showAfterResults();

      return;
    }

    fetchAbort = new AbortController();

    fetchProductSelectorScore(cfg, built.selections, {
      signal: fetchAbort.signal
    })
      .then(function(data) {
        loadingEl.setAttribute("hidden", "");
        resultsPanel.setAttribute("aria-busy", "false");
        if (data && data.success && data.data) {
          let html =
            typeof data.data.results_html === "string"
              ? data.data.results_html
              : "";

          resultsContentEl.innerHTML = html;
          pushProductSelectorEvent(
            cfg,
            "product_selector_results",
            Object.assign(
              {
                // eslint-disable-next-line camelcase
                step_count: stepsTotal
              },
              buildResultsEventPayload(data.data, built.selections)
            )
          );
        } else {
          let errMsg =
            data && data.data && data.data.message
              ? String(data.data.message)
              : "Request failed.";

          resultsContentEl.textContent = errMsg;
        }

        showAfterResults();
      })
      .catch(function(err) {
        if (err && err.name === "AbortError") {
          return;
        }

        loadingEl.setAttribute("hidden", "");
        resultsPanel.setAttribute("aria-busy", "false");
        resultsContentEl.textContent = String(
          err && err.message ? err.message : err
        );
        showAfterResults();
      });
  }

  function trackStepComplete(section) {
    const stepPayload = buildStepCompletePayload(section);

    pushProductSelectorEvent(
      cfg,
      "product_selector_step_complete",
      Object.assign({}, stepPayload, {
        // eslint-disable-next-line camelcase
        step_number: currentStep + 1,
        // eslint-disable-next-line camelcase
        steps_total: stepsTotal,
        // eslint-disable-next-line camelcase
        step_count: stepsTotal
      })
    );
  }

  function goNext() {
    hideInlineError();
    if (currentStep < sections.length - 1) {
      currentStep++;
      updateStepVisibility();
      focusCurrentStepTitle();
    } else {
      finishQuestions();
    }
  }

  syncAllFollowUpVisibility(root);
  updateStepVisibility();

  root.addEventListener("change", function(e) {
    const t = e.target;
    const section = t.closest ? t.closest(SEL.section) : null;

    if (showingIntro || !section || sections[currentStep] !== section) {
      return;
    }

    if (t.matches && t.matches("input[data-ps-main]")) {
      syncFollowUpVisibility(section);
    }
  });

  root.addEventListener("click", function(e) {
    const introCta = e.target.closest && e.target.closest(SEL.introCta);

    if (introCta && introEl && introEl.contains(introCta)) {
      leaveIntro();

      return;
    }

    const resultLink =
      e.target.closest && e.target.closest(SEL.resultProductLink);

    if (resultLink && resultsContentEl.contains(resultLink)) {
      const productId = parseInt(
        resultLink.getAttribute("data-ps-result-product-id"),
        10
      );
      const position = resultLink.getAttribute("data-ps-result-position") || "";
      const productTitle =
        resultLink.getAttribute("data-ps-result-title") || "";

      pushProductSelectorEvent(cfg, "product_selector_result_click", {
        // eslint-disable-next-line camelcase
        product_id: Number.isFinite(productId) ? productId : 0,
        // eslint-disable-next-line camelcase
        product_title: productTitle,
        position: position
      });

      return;
    }

    const btn = e.target.closest && e.target.closest(SEL.confirmBtn);

    if (!btn) {
      return;
    }

    const section = btn.closest(SEL.section);

    if (showingIntro || !section || sections[currentStep] !== section) {
      return;
    }

    if (!isStepComplete(section)) {
      const type = section.getAttribute("data-question-type") || "";

      showInlineError(
        type === "single-choice"
          ? i18n.stepIncompleteSingle
          : i18n.stepIncompleteMultiple
      );

      return;
    }

    trackStepComplete(section);
    goNext();
  });

  if (restartConfirmEl) {
    restartConfirmEl.addEventListener("click", function(e) {
      const t = e.target;

      if (t.closest && t.closest("[data-ps-restart-cancel]")) {
        closeRestartConfirm();

        return;
      }

      if (t.closest && t.closest("[data-ps-restart-confirm]")) {
        pushProductSelectorEvent(cfg, "product_selector_restart", {
          // eslint-disable-next-line camelcase
          from_step: getRestartFromStep(),
          // eslint-disable-next-line camelcase
          step_count: stepsTotal
        });
        resetWizard();
        closeRestartConfirm();
      }
    });
  }

  document.addEventListener("keydown", function(e) {
    if (e.key !== "Escape") {
      return;
    }

    if (!restartConfirmEl || restartConfirmEl.hasAttribute("hidden")) {
      return;
    }

    e.preventDefault();
    closeRestartConfirm();
  });

  restartBtn.addEventListener("click", function() {
    openRestartConfirm();
  });
}
